import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ContentMode, IntelligenceMode, MediaItem } from '@/types';

interface ToastPayload {
  title: string;
  message: string;
}

interface UiState {
  mode: ContentMode;
  /** Dashboard-level intelligence filter (video / image / document). */
  intelligenceMode: IntelligenceMode;
  navScrolled: boolean;
  toastVisible: boolean;
  toastTitle: string;
  toastMessage: string;
  transcriptSearch: string;
  passageSearch: string;
  uploadModalOpen: boolean;
  uploadInProgress: boolean;
  selectedTileIds: string[];
  detailDrawerOpen: boolean;
  detailDrawerItem: MediaItem | null;
  /** Bumped on every openDetailDrawer call — used as a remount key so a
   *  crash in one render attempt doesn't permanently blank the drawer. */
  detailDrawerSeq: number;
  contentEnrichmentOpen: boolean;
}

const initialState: UiState = {
  mode: 'media',
  intelligenceMode: 'video',
  navScrolled: false,
  toastVisible: false,
  toastTitle: '',
  toastMessage: '',
  transcriptSearch: '',
  passageSearch: '',
  uploadModalOpen: false,
  uploadInProgress: false,
  selectedTileIds: [],
  detailDrawerOpen: false,
  detailDrawerItem: null,
  detailDrawerSeq: 0,
  contentEnrichmentOpen: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setMode(state, action: PayloadAction<ContentMode>) {
      state.mode = action.payload;
    },
    setIntelligenceMode(state, action: PayloadAction<IntelligenceMode>) {
      state.intelligenceMode = action.payload;
    },
    setNavScrolled(state, action: PayloadAction<boolean>) {
      state.navScrolled = action.payload;
    },
    showToast(state, action: PayloadAction<ToastPayload>) {
      state.toastVisible = true;
      state.toastTitle = action.payload.title;
      state.toastMessage = action.payload.message;
    },
    hideToast(state) {
      state.toastVisible = false;
    },
    setTranscriptSearch(state, action: PayloadAction<string>) {
      state.transcriptSearch = action.payload;
    },
    setPassageSearch(state, action: PayloadAction<string>) {
      state.passageSearch = action.payload;
    },
    openUploadModal(state) {
      state.uploadModalOpen = true;
    },
    closeUploadModal(state) {
      state.uploadModalOpen = false;
    },
    setUploadInProgress(state, action: PayloadAction<boolean>) {
      state.uploadInProgress = action.payload;
    },
    toggleTileSelection(state, action: PayloadAction<string>) {
      const id = action.payload;
      const idx = state.selectedTileIds.indexOf(id);
      if (idx === -1) {
        state.selectedTileIds.push(id);
      } else {
        state.selectedTileIds.splice(idx, 1);
      }
    },
    clearTileSelection(state) {
      state.selectedTileIds = [];
    },
    openDetailDrawer(state, action: PayloadAction<MediaItem>) {
      state.detailDrawerOpen = true;
      state.detailDrawerItem = action.payload;
      state.detailDrawerSeq += 1;
    },
    closeDetailDrawer(state) {
      state.detailDrawerOpen = false;
    },
    openContentEnrichment(state) {
      state.contentEnrichmentOpen = true;
    },
    closeContentEnrichment(state) {
      state.contentEnrichmentOpen = false;
    },
  },
});

export const {
  setMode,
  setIntelligenceMode,
  setNavScrolled,
  showToast,
  hideToast,
  setTranscriptSearch,
  setPassageSearch,
  openUploadModal,
  closeUploadModal,
  setUploadInProgress,
  toggleTileSelection,
  clearTileSelection,
  openDetailDrawer,
  closeDetailDrawer,
  openContentEnrichment,
  closeContentEnrichment,
} = uiSlice.actions;
export default uiSlice.reducer;
